import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import {
  PORTAL_PERMISSIONS,
  PORTAL_PERMISSION_BUILT,
  PORTAL_PERMISSION_LABELS,
  type PortalPermission,
  type PortalUser,
} from '../../types/portalUser';
import {
  permissionChangeSummary,
  permissionScopeSentence,
  samePermissions,
} from '../../lib/portalPermissionEdit';
import { updatePortalUser } from './api';

// CLIENT PORTAL — change a portal user's features after the invite.
//
// ⚠️⚠️ THE PERSON'S SETTING, NOT THIS ACCOUNT'S. It is opened from one account's
// page and applies to every location the person can see; the scope sentence
// (src/lib/portalPermissionEdit.ts) is the first thing in the dialog and says
// so, with the count, before any checkbox. Do not move it below the boxes.
//
// It goes through the existing PATCH /api/portal-users/:id, which is audited
// (`portal-user-updated`). There is no second write path.

type Props = {
  user: PortalUser;
  accountName: string;
  onClose: () => void;
  onSaved: (user: PortalUser) => void;
};

export function EditPortalPermissionsModal({ user, accountName, onClose, onSaved }: Props) {
  const [permissions, setPermissions] = useState<PortalPermission[]>(user.permissions);

  const mut = useMutation({
    mutationFn: () => updatePortalUser(user.id, { permissions }),
    onSuccess: (updated) => onSaved(updated),
  });

  const toggle = (p: PortalPermission) =>
    setPermissions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const unchanged = samePermissions(user.permissions, permissions);
  const change = permissionChangeSummary(user.permissions, permissions);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-labelledby="edit-portal-permissions-title"
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id="edit-portal-permissions-title" className="mb-1 text-lg font-semibold text-slate-900">
          {user.displayName}'s portal features
        </h2>
        <p className="mb-4 text-sm text-slate-600">{user.email}</p>

        <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {permissionScopeSentence(user.displayName, user.locationCount, accountName)}
        </p>

        <fieldset>
          <legend className="mb-1 block text-sm font-medium text-slate-700">
            What they can see
          </legend>
          <div className="space-y-1.5">
            {PORTAL_PERMISSIONS.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={permissions.includes(p)}
                  onChange={() => toggle(p)}
                  disabled={mut.isPending}
                  className="rounded border-slate-300"
                />
                {PORTAL_PERMISSION_LABELS[p]}
                {!PORTAL_PERMISSION_BUILT[p] && (
                  <span className="text-xs text-slate-400">(not built yet)</span>
                )}
              </label>
            ))}
          </div>
          {permissions.length === 0 && (
            <p className="mt-1.5 text-xs text-slate-500">
              With nothing ticked they can sign in and see the account names, and no more.
            </p>
          )}
        </fieldset>

        {change && <p className="mt-3 text-sm text-slate-700">{change}</p>}

        {mut.error && (
          <p className="mt-3 text-sm text-rose-600">{(mut.error as Error).message}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={unchanged || mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save features'}
          </Button>
        </div>
      </div>
    </div>
  );
}
